/**
 * PdfCacheService - IndexedDB service for caching encrypted PDF pages
 *
 * This service stores encrypted PDF page data (as returned from the API) in IndexedDB
 * for offline access. The encryption/decryption logic remains in PdfRenderer.
 */

interface CachedPage {
  pdfId: string;
  pageNumber: number;
  userId: string;
  encryptedData: ArrayBuffer;
  timestamp: number;
  size: number;
}

interface CacheStats {
  totalSize: number;
  totalPages: number;
  pdfs: Array<{ pdfId: string; pageCount: number; size: number }>;
}

const DB_NAME = 'pdf-cache';
const DB_VERSION = 1;
const STORE_NAME = 'encrypted-pages';

export class PdfCacheService {
  private static dbPromise: Promise<IDBDatabase> | null = null;

  /**
   * Initialize the IndexedDB database
   * Should be called once at app startup
   */
  static async init(): Promise<void> {
    if (this.dbPromise) {
      return;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB open error:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store with composite key
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, {
            keyPath: ['pdfId', 'userId', 'pageNumber']
          });

          // Index for querying all pages of a PDF for a user
          store.createIndex('by-pdf-user', ['pdfId', 'userId'], { unique: false });

          // Index for timestamp-based queries (LRU eviction)
          store.createIndex('by-timestamp', 'timestamp', { unique: false });
        }
      };
    });

    await this.dbPromise;
  }

  /**
   * Get the database instance
   */
  private static async getDB(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      await this.init();
    }
    return this.dbPromise!;
  }

  /**
   * Store encrypted page data from API
   * @param pdfId - PDF identifier
   * @param pageNumber - Page number (1-based)
   * @param encryptedData - Raw encrypted data from API (IV + tag + encrypted PDF)
   * @param userId - User ID for cache isolation
   */
  static async storePage(
    pdfId: string,
    pageNumber: number,
    encryptedData: ArrayBuffer,
    userId: string
  ): Promise<void> {
    try {
      const db = await this.getDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      const cachedPage: CachedPage = {
        pdfId,
        pageNumber,
        userId,
        encryptedData,
        timestamp: Date.now(),
        size: encryptedData.byteLength
      };

      await new Promise<void>((resolve, reject) => {
        const request = store.put(cachedPage);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch (error) {
      console.error('Failed to store page in cache:', error);
      throw error;
    }
  }

  /**
   * Retrieve encrypted page data
   * @param pdfId - PDF identifier
   * @param pageNumber - Page number (1-based)
   * @param userId - User ID
   * @returns Encrypted data or null if not cached
   */
  static async getPage(
    pdfId: string,
    pageNumber: number,
    userId: string
  ): Promise<ArrayBuffer | null> {
    try {
      const db = await this.getDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);

      const result = await new Promise<CachedPage | undefined>((resolve, reject) => {
        const request = store.get([pdfId, userId, pageNumber]);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      return result?.encryptedData || null;
    } catch (error) {
      console.error('Failed to get page from cache:', error);
      return null;
    }
  }

  /**
   * Check if a specific page is cached
   * @param pdfId - PDF identifier
   * @param pageNumber - Page number (1-based)
   * @param userId - User ID
   * @returns True if page is cached
   */
  static async hasPage(
    pdfId: string,
    pageNumber: number,
    userId: string
  ): Promise<boolean> {
    try {
      const db = await this.getDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);

      const result = await new Promise<number | undefined>((resolve, reject) => {
        const request = store.count([pdfId, userId, pageNumber]);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      return result === 1;
    } catch (error) {
      console.error('Failed to check page cache:', error);
      return false;
    }
  }

  /**
   * Get all cached page numbers for a PDF
   * Used for offline indicator in UI
   * @param pdfId - PDF identifier
   * @param userId - User ID
   * @returns Array of cached page numbers
   */
  static async getCachedPages(
    pdfId: string,
    userId: string
  ): Promise<number[]> {
    try {
      const db = await this.getDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('by-pdf-user');

      const pages: number[] = [];

      await new Promise<void>((resolve, reject) => {
        const request = index.openCursor(IDBKeyRange.only([pdfId, userId]));

        request.onsuccess = () => {
          const cursor = request.result;
          if (cursor) {
            const page = cursor.value as CachedPage;
            pages.push(page.pageNumber);
            cursor.continue();
          } else {
            resolve();
          }
        };

        request.onerror = () => reject(request.error);
      });

      return pages.sort((a, b) => a - b);
    } catch (error) {
      console.error('Failed to get cached pages:', error);
      return [];
    }
  }

  /**
   * Delete entire PDF cache for a user
   * @param pdfId - PDF identifier
   * @param userId - User ID
   */
  static async deletePdf(pdfId: string, userId: string): Promise<void> {
    try {
      const db = await this.getDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('by-pdf-user');

      await new Promise<void>((resolve, reject) => {
        const request = index.openCursor(IDBKeyRange.only([pdfId, userId]));

        request.onsuccess = () => {
          const cursor = request.result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          } else {
            resolve();
          }
        };

        request.onerror = () => reject(request.error);
      });

      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch (error) {
      console.error('Failed to delete PDF cache:', error);
      throw error;
    }
  }

  /**
   * Clear all cache (all users, all PDFs)
   */
  static async clearAll(): Promise<void> {
    try {
      const db = await this.getDB();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);

      await new Promise<void>((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch (error) {
      console.error('Failed to clear all cache:', error);
      throw error;
    }
  }

  /**
   * Get cache statistics
   * @param userId - Optional user ID to filter by
   * @returns Cache statistics including size and page count
   */
  static async getCacheStats(userId?: string): Promise<CacheStats> {
    try {
      const db = await this.getDB();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);

      const pdfMap = new Map<string, { pageCount: number; size: number }>();
      let totalSize = 0;
      let totalPages = 0;

      await new Promise<void>((resolve, reject) => {
        const request = store.openCursor();

        request.onsuccess = () => {
          const cursor = request.result;
          if (cursor) {
            const page = cursor.value as CachedPage;

            // Filter by user if specified
            if (!userId || page.userId === userId) {
              totalSize += page.size;
              totalPages++;

              const pdfStats = pdfMap.get(page.pdfId) || { pageCount: 0, size: 0 };
              pdfStats.pageCount++;
              pdfStats.size += page.size;
              pdfMap.set(page.pdfId, pdfStats);
            }

            cursor.continue();
          } else {
            resolve();
          }
        };

        request.onerror = () => reject(request.error);
      });

      const pdfs = Array.from(pdfMap.entries()).map(([pdfId, stats]) => ({
        pdfId,
        pageCount: stats.pageCount,
        size: stats.size
      }));

      return {
        totalSize,
        totalPages,
        pdfs
      };
    } catch (error) {
      console.error('Failed to get cache stats:', error);
      return {
        totalSize: 0,
        totalPages: 0,
        pdfs: []
      };
    }
  }
}
